import { DateInRangesValidator } from "@cervval.js/client-html5-ui/src/editors/editorsWithPicker/dateEditor/dateEditor.validators";
import { APP_SERVICE, AppService } from "@cervval.js/common/services";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { PropertyConfig, PropertyOptionsList } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { EmailValidator } from "@cervval.js/common/src/services/validation.impl/validators/email.validator";
import { MaxLengthValidator } from "@cervval.js/common/src/services/validation.impl/validators/maxLength.validator";
import { MinLengthValidator } from "@cervval.js/common/src/services/validation.impl/validators/minLength.validator";
import { PhoneNumberValidator } from "@cervval.js/common/src/services/validation.impl/validators/phoneNumber.validator";
import { RequiredValidator } from "@cervval.js/common/src/services/validation.impl/validators/required.validator";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { getTodayDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { PatientDTO } from "@sportsante/common-shared/src/data/users/user.dto";
import { patientKeysSorted } from "@sportsante/common-shared/src/data/users/user.tools";
import { DataSourceUID } from "@sportsante/common-shared/src/datasource";
import { I18nPatient } from "@sportsante/common-shared/src/i18n/patient.i18n";

/**
 * Export SportSantePatientDTO into PropertyConfig
 * @param canEdit Boolean. Set it to false to make all exported propertyConfig build disabled. True by default
 * @param disableFields Array of SportSantePatientDTO keys. A key present in this array will always have a forced disabled property
 */
export function exportPatientDTOToPropertyConfig(canEdit: boolean = true, disableFields: (keyof PatientDTO)[] = []): PropertyConfig[] {
    const i18n = I18nPatient();
    const phoneLocale = injector.find<AppService>(APP_SERVICE)?.getLocale().phoneLocale ?? "FR";

    const disabledPropertySet = new Set<keyof PatientDTO>(disableFields);

    const propertyConfigs: PropertyConfig[] = [];
    patientKeysSorted.forEach((key) => {
        const isDisabled = disabledPropertySet.has(key) ? true : canEdit === false;
        let propertyConfig = {} as PropertyConfig;
        const comp = propertyConfig;
        switch (key) {
            case "birthDate":
                propertyConfig = {
                    label: i18n.birthDate.label,
                    propertyName: key,
                    typeUid: PropertyConfigType.DATE,
                    options: {
                        placeholder: i18n.birthDate.optionsPlaceholder,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(DateInRangesValidator, {
                            ranges: [{
                                start: getTodayDate().beginOf("day").subtract({ year: 125 }).uts,
                                finish: getTodayDate().beginOf("day").subtract({ year: 18 }).uts
                            }]
                        })
                    };
                }
                break;
            case "gender":
                propertyConfig = <PropertyConfig<PropertyConfigType, PropertyOptionsList>>{
                    ...i18n.civility,
                    propertyName: key,
                    typeUid: PropertyConfigType.LISTSELECT,
                    options: {
                        itemList: DataSourceUID.CIVILITY,
                        emptyValue: { label: i18n.genderEmpty.label },
                        disabled: isDisabled,
                        filterDisabled: true
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true)
                    };
                }
                break;
            case "givenName":
                propertyConfig = {
                    label: i18n.givenName.label,
                    propertyName: key,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        placeholder: i18n.givenName.optionsPlaceholder,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(MinLengthValidator, 3)
                    };
                }
                break;
            case "familyName":
                propertyConfig = {
                    label: i18n.familyName.label,
                    propertyName: key,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        placeholder: i18n.familyName.optionsPlaceholder,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(MinLengthValidator, 3)
                    };
                }
                break;
            case "email":
                propertyConfig = {
                    label: i18n.email.label,
                    propertyName: key,
                    typeUid: PropertyConfigType.EMAIL,
                    options: {
                        placeholder: i18n.email.optionsPlaceholder,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(EmailValidator, true)
                    };
                }
                break;
            case "phoneNumber":
                propertyConfig = {
                    label: i18n.phoneNumber.label,
                    propertyName: key,
                    typeUid: PropertyConfigType.PHONE_NUMBER,
                    options: {
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(PhoneNumberValidator, phoneLocale)
                    };
                }
                break;
            default:
                break;
        }
        if (propertyConfig !== comp) {
            propertyConfigs.push(propertyConfig);
        }
    });
    return propertyConfigs;
}

/** 
 * Return a property config to visualize / edit the MSS id of a patient
 * @param [canEdit=true] Boolean flag to know if the property should be editable. Default value is `true`
 */
export function exportIdMSSPropertyConfig(canEdit: boolean = true): PropertyConfig {
    const i18n = I18nPatient();
    return {
        label: i18n.idMss.label,
        propertyName: "idMSS",
        typeUid: PropertyConfigType.STRING,
        options: {
            disabled: !canEdit
        },
        validators: !canEdit ? undefined : { ...validatorEntry(MaxLengthValidator, 256) }
    };
}